import {
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  EarthLock,
  FileText,
  Globe,
  HardDrive,
  Headphones,
  LaptopIcon,
  Paintbrush,
  Server,
} from "lucide-react";
import { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Accounts } from "@/app/components/settings/pages/accounts";
import { Appearance } from "@/app/components/settings/pages/appearance";
import { Audio } from "@/app/components/settings/pages/audio";
import { Content } from "@/app/components/settings/pages/content";
import { Desktop } from "@/app/components/settings/pages/desktop";
import { Language } from "@/app/components/settings/pages/language";
import { Privacy } from "@/app/components/settings/pages/privacy";
import { SettingsOptions } from "@/app/components/settings/options";
import { Storage } from "@/app/components/settings/pages/storage";
import { ServerSettings } from "@/app/components/settings/pages/server";
import { ScrollArea } from "@/app/components/ui/scroll-area";
import { MobilePageHeader } from "@/app/components/header/mobile-page-header";
import { isDesktop } from "@/utils/desktop";

interface CategoryItem {
  id: SettingsOptions;
  icon: ComponentType<{ className?: string }>;
}

const accountsOption: CategoryItem = { id: "accounts", icon: CircleUserRound };
const desktopOption: CategoryItem = { id: "desktop", icon: LaptopIcon };

const categories: CategoryItem[] = [
  { id: "server", icon: Server },
  { id: "appearance", icon: Paintbrush },
  { id: "language", icon: Globe },
  { id: "audio", icon: Headphones },
  { id: "content", icon: FileText },
  { id: "storage", icon: HardDrive },
  ...(isDesktop() ? [accountsOption, desktopOption] : []),
  { id: "privacy", icon: EarthLock },
];

const pages: Record<SettingsOptions, () => JSX.Element> = {
  server: () => <ServerSettings />,
  appearance: () => <Appearance />,
  audio: () => <Audio />,
  language: () => <Language />,
  content: () => <Content />,
  storage: () => <Storage />,
  accounts: () => <Accounts />,
  desktop: () => <Desktop />,
  privacy: () => <Privacy />,
};

export default function MobileSettings() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = searchParams.get("page") as SettingsOptions | null;

  function openPage(id: SettingsOptions) {
    setSearchParams({ page: id });
  }

  function goBack() {
    setSearchParams({});
  }

  if (currentPage && pages[currentPage]) {
    return (
      <div className="flex flex-col w-full h-full">
        <div
          className="flex items-center gap-2 px-4 py-3 border-b"
          style={{
            paddingTop: "max(0.75rem, var(--safe-area-top))",
            paddingLeft: "max(1rem, var(--safe-area-left))",
            paddingRight: "max(1rem, var(--safe-area-right))",
          }}
        >
          <button
            type="button"
            onClick={goBack}
            className="flex min-h-11 items-center gap-1 text-sm text-muted-foreground active:text-foreground"
          >
            <ChevronLeft className="w-4 h-4" />
            {t("settings.label")}
          </button>
          <span className="text-sm font-medium ml-1">
            {t(`settings.options.${currentPage}`)}
          </span>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-4">{pages[currentPage]()}</div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <MobilePageHeader variant="root" title={t("settings.label")} />
      <div className="flex flex-col">
        {categories.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => openPage(item.id)}
            className="flex items-center gap-3 px-4 py-3.5 active:bg-accent/50 border-b last:border-b-0"
          >
            <item.icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <span className="flex-1 text-sm text-left">
              {t(`settings.options.${item.id}`)}
            </span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}
